import { useState } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Award, Edit2, Save, X, Users } from 'lucide-react';
import { Skill, updateSkill } from '@/services/staffService';

interface SkillDetailsSheetProps {
  skill: Skill | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void;
}

export function SkillDetailsSheet({ skill, isOpen, onClose, onUpdate }: SkillDetailsSheetProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    name: skill?.name || '',
    category: skill?.category || '',
    description: skill?.description || '',
  });

  if (!skill) return null;

  // Reset form when skill changes or when starting edit
  const startEdit = () => {
    setFormData({
      name: skill.name,
      category: skill.category,
      description: skill.description,
    });
    setIsEditing(true);
    setError(null);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setError(null);
  };

  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      await updateSkill(skill.id, {
        ...skill,
        ...formData,
      });

      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update skill');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[400px] sm:w-[540px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Award className="h-5 w-5" />
              {skill.name}
            </div>
            {!isEditing ? (
              <Button
                variant="outline"
                size="sm"
                onClick={startEdit}
                className="text-primary-500 border-primary-500 hover:bg-primary-500/10"
              >
                <Edit2 className="h-4 w-4 mr-2" />
                Edit
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={cancelEdit}
                  disabled={isSubmitting}
                  className="text-neutral-400 border-neutral-600 hover:bg-neutral-800"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={isSubmitting}
                  className="bg-primary-500 text-black hover:bg-primary-600"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              </div>
            )}
          </SheetTitle>
          <SheetDescription>Skill details and proficiency information</SheetDescription>
        </SheetHeader>

        {error && (
          <div className="mt-4 p-3 bg-red-900/20 border border-red-500/50 rounded-md text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="mt-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-white">Basic Information</h3>
            {!isEditing ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Award className="h-4 w-4 text-neutral-400" />
                  <span className="text-neutral-400">Category:</span>
                  <Badge variant="outline" className="text-primary-400 border-primary-500/30">
                    {skill.category}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Users className="h-4 w-4 text-neutral-400" />
                  <span className="text-neutral-400">Staff with this skill:</span>
                  <span className="text-white">{skill.staffCount || 0} employees</span>
                </div>
                {skill.description && (
                  <div className="text-sm">
                    <span className="text-neutral-400">Description:</span>
                    <p className="text-white mt-1">{skill.description}</p>
                  </div>
                )}
                {skill.avgProficiency && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-neutral-400">Average Proficiency:</span>
                    <span className="text-white">{skill.avgProficiency}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Skill Name *</Label>
                  <Input
                    id="edit-name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="bg-neutral-900 border-neutral-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-category">Category *</Label>
                  <select
                    id="edit-category"
                    value={formData.category}
                    onChange={(e) => handleChange('category', e.target.value)}
                    className="w-full bg-neutral-900 border-neutral-700 text-white rounded-md px-3 py-2"
                  >
                    <option value="Technical">Technical</option>
                    <option value="Soft Skills">Soft Skills</option>
                    <option value="Domain Knowledge">Domain Knowledge</option>
                    <option value="Compliance">Compliance</option>
                    <option value="Leadership">Leadership</option>
                    <option value="Certification">Certification</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-description">Description</Label>
                  <textarea
                    id="edit-description"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    className="w-full bg-neutral-900 border-neutral-700 text-white rounded-md px-3 py-2 min-h-[100px]"
                    placeholder="Describe this skill..."
                  />
                </div>
              </div>
            )}
          </div>

          {/* Required for Processes */}
          {skill.requiredForProcesses && skill.requiredForProcesses.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-white">Required for Processes</h3>
              <div className="flex flex-wrap gap-2">
                {skill.requiredForProcesses.map((processId) => (
                  <Badge key={processId} variant="outline" className="text-primary-400 border-primary-500/30">
                    Process #{processId.slice(0, 8)}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-white">Metadata</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-neutral-400">Created:</span>
                <span className="text-white">
                  {new Date(skill.createdAt).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-400">Last Updated:</span>
                <span className="text-white">
                  {new Date(skill.updatedAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
